import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card } from '../../ui/card';
import { Button } from '../../ui/button';

const ROLE_OPTIONS = [
  ['advisory_partner', 'Advisory Partner'],
  ['guide_partner', 'Guide Partner'],
];

async function errorMessage(response, fallback) {
  try {
    const parsed = await response.json();
    return typeof parsed?.detail === 'string' ? parsed.detail : fallback;
  } catch (_) {
    return fallback;
  }
}

export default function InvitedEmailsPanel({ adminFetch }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailsText, setEmailsText] = useState('');
  const [roles, setRoles] = useState(['advisory_partner', 'guide_partner']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminFetch('/admin/invited-emails');
      if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load invited emails'));
      const payload = await response.json().catch(() => ({}));
      setInvites(Array.isArray(payload?.invites) ? payload.invites : []);
    } catch (error) {
      toast.error(error?.message || 'Failed to load invited emails');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRole = (role) => {
    setRoles((current) => (current.includes(role) ? current.filter((item) => item !== role) : [...current, role]));
  };

  const addEmails = async () => {
    const emails = emailsText.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean);
    if (!emails.length) {
      toast.error('Enter at least one email.');
      return;
    }
    if (!roles.length) {
      toast.error('Choose at least one role.');
      return;
    }
    setSaving(true);
    try {
      const response = await adminFetch('/admin/invited-emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, roles }),
      });
      if (!response.ok) throw new Error(await errorMessage(response, 'Failed to save invited emails'));
      toast.success(`Saved ${emails.length} invited ${emails.length === 1 ? 'email' : 'emails'}.`);
      setEmailsText('');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Failed to save invited emails');
    } finally {
      setSaving(false);
    }
  };

  const removeInvite = async (invite) => {
    try {
      const response = await adminFetch(`/admin/invited-emails/${encodeURIComponent(invite.invite_id)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await errorMessage(response, 'Failed to remove invite'));
      toast.success('Invite removed. Roles already given stay in place.');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Failed to remove invite');
    }
  };

  return (
    <Card className="border border-[#2a2a32] bg-[#16161a] p-6">
      <h3 className="text-lg font-semibold text-white">Invited emails</h3>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-300">
        Anyone listed here gets these roles automatically the first time they sign in with a verified email, and
        on any later sign-in if a role is missing. Removing an entry does not take roles away from an account that already has them.
      </p>

      <div className="mt-4 space-y-3">
        <textarea
          value={emailsText}
          onChange={(event) => setEmailsText(event.target.value)}
          rows={3}
          placeholder="Paste one or more emails, separated by spaces, commas or new lines"
          className="w-full rounded-md border border-[#2a2a32] bg-[#0f0f12] p-3 text-sm text-white"
        />
        <div className="flex flex-wrap items-center gap-4">
          {ROLE_OPTIONS.map(([role, label]) => (
            <label key={role} className="flex items-center gap-2 text-sm text-gray-200">
              <input type="checkbox" checked={roles.includes(role)} onChange={() => toggleRole(role)} />
              {label}
            </label>
          ))}
          <Button onClick={addEmails} disabled={saving} className="ml-auto">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Add to list
          </Button>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-aqua" />
        ) : invites.length === 0 ? (
          <p className="text-sm text-gray-400">No invited emails yet.</p>
        ) : (
          <ul className="divide-y divide-[#2a2a32]">
            {invites.map((invite) => (
              <li key={invite.invite_id} className="flex items-center justify-between gap-3 py-2 text-sm text-gray-200">
                <span className="min-w-0 truncate">{invite.email_normalized}</span>
                <span className="shrink-0 text-xs text-gray-400">{(invite.roles || []).join(', ')}</span>
                <Button
                  onClick={() => removeInvite(invite)}
                  variant="outline"
                  className="shrink-0 border-rose-500/25 text-rose-200 hover:bg-rose-500/10"
                  aria-label={`Remove ${invite.email_normalized}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
